import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Checkbox, FormControl, FormControlLabel, FormHelperText, InputAdornment, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import axios from "axios"
import { Auth } from "aws-amplify";

// import GroupAddIcon from '@mui/icons-material/GroupAdd';

const SCHOOL_CODE_FORMAT = {
  NIC: "0",
  UDISE: "1",
  SELF: "2",
};

const getSchoolCodeLength = (format) =>
  format === SCHOOL_CODE_FORMAT.NIC
    ? "6"
    : format === SCHOOL_CODE_FORMAT.UDISE
    ? "11"
    : "";

const generateSchoolCodeExample = (digits) => {
  const length = Number(digits);
  if (!length || length < 1 || length > 11) return "";

  return Array.from(
    { length },
    () => Math.floor(Math.random() * 10)
  ).join("");
};

const AddOrganization = ({setToastOpen, setToastType, masterAdminInfo, orgsData, data, setData, registerValidator, registerSubmitter }) => {
  // const [loading, setLoading] = useState(true)

  const orgPrefixes = useMemo(() => 
    new Set(orgsData.map((item) => item.org)),
    [orgsData]
  );

  const orgDomains = useMemo(() => {
    return new Set(
      orgsData.flatMap((item) => [item.emailDomain, item.schoolEmailDomain]).filter(Boolean)
    );
  }, [orgsData]);

  const [formData, setFormData] = useState(data);
  const [errors, setErrors] = useState({});

  const validateField = useCallback((field, value) => {
    let error = "";

    switch (field) {
      case "prefix":
        if (!/^[A-Za-z]{3}$/.test(value)) {
          error = "Prefix should contain only 3 letters.";
        }
        else if (orgPrefixes.has(value)){
          error = "Prefix `" + value + "` is already in use for some other org."
        }
        break;

      case "name":
        if (!/^(?!\s)([A-Za-z0-9 .'\-_]){1,128}$/.test(value)) {
          error = "Name should only contain alphabets, numbers, space, hyphen, underscore, apostrophe and full stop upto 128 characters.";
        }
        break;

      case "state":
        if (!/^(?!\s)([A-Za-z\s]){1,64}$/.test(value)) {
          error = "State should contain only alphabets upto 64 characters.";
        }
        break;

      case "city":
        if (value && !/^(?!\s)([A-Za-z\s]){1,64}$/.test(value)) {
          error = "City should contain only alphabets upto 64 characters.";
        }
        break;

      case "password":
        if (!/^([A-Za-z0-9]){6,10}$/.test(value)) {
          error = "Password should be alphanumeric with 6 to 10 characters";
        }
        break;

      case "schoolCodeLength":
        if (value && !/^(1[0-1]|[1-9])$/.test(value)) {
          error = "Only numeric values allowed from 1 to 11";
        }
        break;
      
      case "emailDomain":
      case "schoolEmailDomain":
        if (value && !/^(?!-)(?!.*\.\.)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/.test(value)) {
          error = "Enter a valid domain.";
        }
        else if (orgDomains.has(value)) {
          error = "Domain already in use.";
        }
        break;
      
      case "testName":
        if (!/^(?!\s)([A-Za-z0-9 \-_]){1,64}$/.test(value)) {
          error = "Test Name should only contain alphabets, numbers, space, hyphen and underscore upto 64 characters.";
        }
        break;

      default:
        break;
    }

    return error;
  }, [orgPrefixes, orgDomains]);

  const validateStep1 = useCallback(() => {
    const newErrors = {};

    const requiredFields = [
      "prefix",
      "name",
      "state",
      "emailDomain",
      "password",
      "schoolCodeFormat",
      "schoolCodeLength",
      "facilitatorLogin",
      "testName",
      "testLangs"
    ];

    requiredFields.forEach((field) => {
      const value = formData[field];

      const isEmptyArray =
        Array.isArray(value) && value.length === 0;

      const isEmpty =
        value === undefined ||
        value === null ||
        value.toString().trim() === "";

      if (isEmpty || isEmptyArray) {
        newErrors[field] = "This field is required";
      } else {
        const error = validateField(field, value);

        if (error) {
          newErrors[field] = error;
        }
      }
    });

    // Optional fields validation
    ["city", "schoolEmailDomain"].forEach((field) => {
      if (formData[field]) {
        const error = validateField(field, formData[field]);

        if (error) {
          newErrors[field] = error;
        }
      }
    });

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }, [formData, validateField]);

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [field]: value,
      };

      // Auto-set school code length based on format
      if (field === "schoolCodeFormat") {
        updatedData.schoolCodeLength = getSchoolCodeLength(value);
      }
      return updatedData;
    });

    setErrors((prev) => {
      const updatedErrors = {
        ...prev,
        [field]: validateField(field, value),
      };
      // Validate schoolCodeLength if format changes
      if (field === "schoolCodeFormat") {
        updatedErrors.schoolCodeLength = validateField(
          "schoolCodeLength",
          getSchoolCodeLength(value)
        );
      }

      return updatedErrors;
    });
  }, [validateField]);

  useEffect(() => {
    setData(formData);
  }, [formData, setData]);

  useEffect(() => {
    registerValidator(validateStep1);
  }, [registerValidator, validateStep1]);

  const schoolCodeExample = useMemo(() => {
    return generateSchoolCodeExample(
      formData.schoolCodeLength
    );
  }, [formData.schoolCodeLength]);

  const exampleData = useMemo(() => {
    if (
      !formData.prefix ||
      !formData.emailDomain ||
      !formData.schoolCodeLength
    ) {
      return null;
    }

    const domain =
      formData.schoolEmailDomain || formData.emailDomain;

    const schoolCode = schoolCodeExample;

    return {
      superAdminEmail: `admin@${formData.emailDomain}`,

      schoolId: `${formData.prefix}${schoolCode}1`,

      adminEmail: formData.singleSchool
        ? `school@${domain}`
        : `SH${schoolCode}@${domain}`,

      classIds: [
        `${formData.prefix}${schoolCode}103A`,
        `${formData.prefix}${schoolCode}103B`,
      ],

      facilitatorEmail:
        formData.facilitatorLogin === "1" ? 
          formData.singleSchool ? `class@${domain}`
            : `${schoolCode}@${domain}`
        : 
          formData.singleSchool ? `3A@${domain}, 3B@${domain}`
            : `${schoolCode}_3A@${domain}, ${schoolCode}_3B@${domain}`

    };
  }, [formData, schoolCodeExample]);

  const submitStep1 = useCallback(async () => {
    try {
      const session = await Auth.currentSession();
      const jwtToken = session.idToken.jwtToken;
      const masterAdminId = masterAdminInfo.masterAdmin.masterAdminId
      
      const createResponse =  await axios.post("https://zk1128nq2f.execute-api.ap-south-1.amazonaws.com/alpha/masterAdmin/"+masterAdminId+"/orgs",
        formData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': jwtToken
        }
      });
      // console.log("Upload Response: ",createResponse.data)
      if(createResponse.data.status === "success") {
        // alert("Deletion Successful: \n1. All student entries have been deleted. \n2. Please ensure that teachers from your class logout and login again on the TARA Android App so that they can see the updated student list.");
        setToastType(["success", "Org Created Successfully!"])
        setToastOpen(true)
        return true;
      }
      else{
        setToastType(["error", "Org not created due to error!"])
        setToastOpen(true)
        return false;
      }
    }
    catch (error) {
      console.log(error)
      setToastType(["error", "Org not created due to error!"])
      setToastOpen(true)
      return false;
    }
  }, [formData, masterAdminInfo, setToastOpen, setToastType]);

  useEffect(() => {
    registerSubmitter(submitStep1);
  }, [registerSubmitter, submitStep1]);

  return (
    <>
      <div>
        {/* <h2>Add Organization</h2> */}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "20px",
          }}
        >
          <TextField
            label="Prefix"
            value={formData.prefix}
            onChange={(e) =>
              handleChange("prefix", e.target.value.toUpperCase())
            }
            error={!!errors.prefix}
            helperText={errors.prefix}
            fullWidth
            required
          />
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) =>
              handleChange("name", e.target.value)
            }
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            required
          />
          <TextField
            label="State"
            value={formData.state}
            onChange={(e) =>
              handleChange("state", e.target.value.toUpperCase())
            }
            error={!!errors.state}
            helperText={errors.state}
            fullWidth
            required
          />
          <TextField
            label="City (Optional)"
            value={formData.city}
            onChange={(e) =>
              handleChange("city", e.target.value.toUpperCase())
            }
            error={!!errors.city}
            helperText={errors.city}
            fullWidth
          />
          <TextField
            label="Email Domain"
            placeholder="example.com"
            InputProps={{
              startAdornment: <InputAdornment position="start">@</InputAdornment>,
            }}
            value={formData.emailDomain}
            onChange={(e) =>
              handleChange("emailDomain", e.target.value.toLowerCase())
            }
            error={!!errors.emailDomain}
            helperText={errors.emailDomain}
            fullWidth
            required
          />
          <TextField
            label="School Email Domain (Optional)"
            placeholder="school.com"
            InputProps={{
              startAdornment: <InputAdornment position="start">@</InputAdornment>,
            }}
            value={formData.schoolEmailDomain}
            onChange={(e) =>
              handleChange("schoolEmailDomain", e.target.value.toLowerCase())
            }
            error={!!errors.schoolEmailDomain}
            helperText={errors.schoolEmailDomain}
            fullWidth
          />
          <TextField
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) =>
              handleChange("password", e.target.value)
            }
            error={!!errors.password}
            helperText={errors.password}
            fullWidth
            required
          />
          <FormControl fullWidth error={!!errors.schoolCodeFormat}>
            <InputLabel>
              School Code Format
            </InputLabel>
            <Select
              value={formData.schoolCodeFormat}
              label="School Code Format"
              onChange={(e) =>
                handleChange("schoolCodeFormat", e.target.value)
              }
              required
            >
              <MenuItem value="0">
                NIC
              </MenuItem>
              <MenuItem value="1">
                UDISE
              </MenuItem>
              <MenuItem value="2">
                SELF
              </MenuItem>
            </Select>
            <FormHelperText>{errors.schoolCodeFormat}</FormHelperText>
          </FormControl>
          <TextField
            label="School Code Length"
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              min: 1,
              max: 11,
            }}
            value={formData.schoolCodeLength}
            disabled={formData.schoolCodeFormat !== SCHOOL_CODE_FORMAT.SELF}
            onChange={(e) =>
              handleChange("schoolCodeLength", e.target.value)
            }
            error={!!errors.schoolCodeLength}
            helperText={errors.schoolCodeLength ? errors.schoolCodeLength : !formData.schoolCodeFormat ? "Select School Code Format" : "" }
            fullWidth
            required
          />
          <FormControl fullWidth error={!!errors.facilitatorLogin}>
            <InputLabel>
              Facilitator Login
            </InputLabel>
            <Select
              value={formData.facilitatorLogin}
              label="Facilitator Login"
              onChange={(e) =>
                handleChange("facilitatorLogin", e.target.value)
              }
              required
            >
              <MenuItem value="1">
                Single Facilitator
              </MenuItem>
              <MenuItem value="2">
                Facilitator For Each Class
              </MenuItem>
            </Select>
            <FormHelperText>{errors.facilitatorLogin}</FormHelperText>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.singleSchool}
                onChange={(e) =>
                  handleChange("singleSchool", e.target.checked)
                }
              />
            }
            label="Single School"
          />
          <TextField
            label="Initial Test Name"
            value={formData.testName}
            onChange={(e) =>
              handleChange("testName", e.target.value)
            }
            error={!!errors.testName}
            helperText={errors.testName}
            fullWidth
            required
          />
          <FormControl sx={{ m: 1, minWidth: 300}} error={!!errors.testLangs}>
            <InputLabel>
              Initial Test Languages
            </InputLabel>
            <Select
              value={formData.testLangs}
              renderValue={(selected) => selected.map(item => masterAdminInfo.allLangs[item]).join(', ')}
              label="Initial Test Languages"
              onChange={(e) => 
                handleChange("testLangs", e.target.value)
              }
              autoWidth
              multiple
              required
            >
              {masterAdminInfo.allLangs &&
                Object.keys(masterAdminInfo.allLangs).sort().map((item, i) => {
                return <MenuItem key={i} value={item}>
                    <Checkbox checked={formData.testLangs.includes(item)} />{masterAdminInfo.allLangs[item]}
                  </MenuItem>
                })
                
              }
            </Select>
            <FormHelperText>{errors.testLangs}</FormHelperText>
          </FormControl>
        </div>
        <Alert
          severity="info"
          sx={{ mt: 2 }}
        >
          <strong>Example Ids:</strong>
          {exampleData && formData.prefix && formData.emailDomain && formData.schoolCodeFormat && formData.schoolCodeLength && formData.facilitatorLogin && 
            <>
              <p style={{marginTop: "0.5rem"}}>
                <b>Super Admin Email:</b>
                {" "}
                {exampleData.superAdminEmail}
              </p>
              <p style={{marginTop: "0.75rem"}}>
                <b>Sample School Code:</b>
                {" "}
                {schoolCodeExample}
              </p>
              <p style={{marginTop: "0.25rem"}}>
                <b>SchoolId:</b>
                {" "}
                {exampleData.schoolId} {("where last digit denotes shift")}
              </p>
              <p style={{marginTop: "0.25rem"}}>
                <b>Admin Email:</b>
                {" "}
                {exampleData.adminEmail}
              </p>
              <p style={{marginTop: "0.75rem"}}>
                <b>Sample classes in school:</b>
                {" "} 3A, 3B
              </p>
              <p style={{marginTop: "0.25rem"}}>
                <b>ClassId:</b>
                {" "}
                {exampleData.classIds.join(", ")}
              </p>
              <p style={{marginTop: "0.25rem"}}>
                <b>Facilitator Email:</b>
                {" "}
                {
                  exampleData.facilitatorEmail
                }
              </p>
            </>
          }
        </Alert>
      </div>
    </>
  );
};

export default AddOrganization;